import { createContext, useContext, useEffect, useMemo, useState } from 'react'

import { FALLBACK_LOCALE } from '@src/config/localisation/locale-config.constants'
import type { TAbsoluteLocale, TCountry, TRegionDetail } from '@src/config/localisation/locale-config.types'
import { noop } from '@src/utils/functions/utils'
import { setLocalisationDataInStorage } from '@src/utils/general.utils'

import useLocaleContextHandlers from './hooks/useLocaleContextHandlers'
import useRegionSwitchHandler from './hooks/useRegionSwitchHandler'
import withI18nGate from './with-i18n-gate.hoc'

export const LocaleContext = createContext<{
  locale: TAbsoluteLocale
  regionDetails: TRegionDetail
  isLocaleModalOpen: boolean
  setIsLocaleModalOpen: (isLocaleModalOpen: boolean) => void
  setLocale: (
    locale: TAbsoluteLocale,
    clientSide?: boolean,
    shouldUpdateCommunicationPreference?: boolean,
  ) => Promise<void> | void
  isLocaleConfirmationModalOpen: boolean
  showConfirmationModal: (confirmationLocale: TAbsoluteLocale, callback: () => void) => void
  closeConfirmationModal: (confirm: boolean) => void
  confirmationLocale: TAbsoluteLocale | TCountry | null
  onConfirm: () => void
  isLocaleUpdating: boolean
}>({
  locale: FALLBACK_LOCALE,
  regionDetails: { regions: {}, regionCodes: [] },
  isLocaleModalOpen: false,
  setIsLocaleModalOpen: noop,
  setLocale: noop,
  isLocaleConfirmationModalOpen: false,
  showConfirmationModal: noop,
  closeConfirmationModal: noop,
  confirmationLocale: null,
  onConfirm: noop,
  isLocaleUpdating: false,
})

export const LocaleContextProvider = withI18nGate(
  ({
    children,
    initialLocale,
    regionDetails,
  }: {
    initialLocale: TAbsoluteLocale
    children: React.ReactNode
    regionDetails: TRegionDetail
  }) => {
    const [isLocaleModalOpen, setIsLocaleModalOpen] = useState(false)
    const [confirmationLocale, setConfirmationLocale] = useState<TAbsoluteLocale | TCountry | null>(null)
    const [isLocaleConfirmationModalOpen, setIsLocaleConfirmationModalOpen] = useState(false)
    const [locale, setLocale] = useState(initialLocale)
    //this is required because locale is being updated through multiple entrypoints-locale-switch-icon, locale-switch-modal, locale-switch-confirmation-popup and url so we need to make sure that while one of these is updating the locale, the other ones are also not updating the locale
    // const [isLocaleUpdating, setIsLocaleUpdating] = useState(false)

    useEffect(() => {
      setLocalisationDataInStorage(initialLocale, regionDetails)
    }, [initialLocale, regionDetails])

    const { handleSetLocale, closeConfirmationModal, showConfirmationModal, onConfirm } = useLocaleContextHandlers({
      setIsLocaleConfirmationModalOpen,
      setConfirmationLocale,
      regionDetails,
      setLocale,
    })

    useRegionSwitchHandler({
      locale,
      regionDetails,
      showConfirmationModal,
    })

    const providerValue = useMemo(() => {
      return {
        locale,
        isLocaleModalOpen,
        setIsLocaleModalOpen,
        setLocale: handleSetLocale,
        regionDetails,
        isLocaleConfirmationModalOpen,
        confirmationLocale,
        showConfirmationModal,
        closeConfirmationModal,
        onConfirm,
      }
    }, [
      locale,
      isLocaleModalOpen,
      handleSetLocale,
      regionDetails,
      isLocaleConfirmationModalOpen,
      confirmationLocale,
      showConfirmationModal,
      closeConfirmationModal,
      onConfirm,
    ])

    return <LocaleContext.Provider value={providerValue}>{children}</LocaleContext.Provider>
  },
  true,
)

export const useLocaleContext = () => {
  return useContext(LocaleContext)
}
